import { useMemo } from 'react';

import {
  getEntityTypeLabel,
  getEntityTypePrincipalLabel,
  validateEntitySuggestion,
} from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewColumnData } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { AISuggestionReviewRow } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewRow';
import { MarkSuggestionAsDuplicateAction } from '@/modules/aiSuggestionReviewCard/components/MarkSuggestionAsDuplicateAction';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

interface AISuggestionReviewEntityRowProps {
  suggestion: AiSuggestionForReviewCard;
}

/**
 * A row that displays an entity suggestion for review.
 *
 * Normalizes the suggestion data into the following columns:
 * - Display name
 * - Entity type
 * - Grantor(s)
 */
export function AISuggestionReviewEntityRow({
  suggestion,
}: AISuggestionReviewEntityRowProps) {
  const {
    principalNodesById,
    onReviewClick,
    onDeleteClick,
    onCreateClick,
    onSuggestionMarkedDuplicate,
  } = useAISuggestionReviewCardContext();

  const { columnsData, hasWarning } = useMemo<{
    columnsData: AISuggestionReviewColumnData[];
    hasWarning: boolean;
  }>(() => {
    const suggestionData = suggestion.onboardingEntity;
    if (!suggestionData) return { columnsData: [], hasWarning: false };

    const entityType = suggestionData.entityKind
      ? getEntityTypeFromEntityKind(suggestionData.entityKind)
      : undefined;

    // Get the appropriate label based on entity type
    const principalLabel = getEntityTypePrincipalLabel(entityType);

    // Resolve principals directly from `principalNodeIDs` against the
    // household node map. `hasWarning` already covers the no-principals case.
    const validation = validateEntitySuggestion(suggestion, principalNodesById);

    const displayPrincipalCellValues =
      validation.principalCellValues.length === 0
        ? [
            {
              displayText: 'Unknown',
              showWarning: true,
              warningTooltip: `No ${principalLabel.toLowerCase()} suggested. Add a ${principalLabel.toLowerCase()} to create this entity.`,
            },
          ]
        : validation.principalCellValues;

    return {
      columnsData: [
        {
          label: 'Display name',
          values: [
            {
              displayText:
                suggestionData.displayName ??
                sentenceCaseWithAcronyms(suggestionData.entityName || ''),
              showWarning: false,
              notes: suggestionData.notes,
            },
          ],
        },
        {
          label: 'Entity type',
          values: [
            {
              displayText: entityType ? getEntityTypeLabel(entityType) : '-',
              showWarning: false,
            },
          ],
        },
        {
          label: principalLabel,
          values: displayPrincipalCellValues,
        },
      ],
      hasWarning: validation.hasWarning,
    };
  }, [suggestion, principalNodesById]);

  if (!suggestion.onboardingEntity) {
    return null;
  }

  return (
    <AISuggestionReviewRow
      suggestionID={suggestion.id}
      rowHasWarning={hasWarning}
      columnsData={columnsData}
      onCreateClick={onCreateClick}
      onReviewClick={onReviewClick}
      onDeleteClick={onDeleteClick}
      renderAdditionalAction={({ disabled }) => (
        <MarkSuggestionAsDuplicateAction
          suggestion={suggestion}
          disabled={disabled}
          onMarkedDuplicate={onSuggestionMarkedDuplicate}
        />
      )}
    />
  );
}
